import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogType,
  DialogFooter,
  PrimaryButton,
  DefaultButton,
  Checkbox,
  Stack,
  Text,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  ProgressIndicator,
  IStackTokens
} from '@fluentui/react';
import { Table24Regular, CheckboxChecked24Regular, ArrowClockwise24Regular } from '@fluentui/react-icons';
import { authenticatedFetch } from '../../utils/authApi';

export interface SheetPreview {
  name: string;
  rows: number;
  columns: number;
  column_names?: string[];
  estimated_chunks?: number;
}

export interface SheetPreviewResult {
  success: boolean;
  filename: string;
  file_size: number;
  sheets: SheetPreview[];
  total_rows: number;
  total_columns: number;
  estimated_processing_time_seconds: number;
  recommendation: string;
}

interface ExcelSheetSelectorProps {
  /** The Excel file to preview (null when not in use) */
  file: File | null;
  /** Called when user confirms sheet selection */
  onConfirm: (selectedSheets: string[], processAll: boolean) => void;
  /** Called when user cancels */
  onCancel: () => void;
  /** Whether the dialog is open */
  isOpen: boolean;
}

const stackTokens: IStackTokens = { childrenGap: 12 };

export const ExcelSheetSelector: React.FC<ExcelSheetSelectorProps> = ({
  file,
  onConfirm,
  onCancel,
  isOpen
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<SheetPreviewResult | null>(null);
  const [selectedSheets, setSelectedSheets] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);  // Default: no sheets selected

  // Load sheet preview when dialog opens with a file
  // Only triggers when isOpen becomes true (component is always mounted)
  useEffect(() => {
    if (isOpen && file) {
      console.log('[ExcelSheetSelector] Loading preview for', file.name);
      loadSheetPreview();
    }
    
    // Reset state when dialog closes
    if (!isOpen) {
      setPreview(null);
      setSelectedSheets(new Set());
      setSelectAll(false);
      setError(null);
      setLoading(false);
    }
  }, [isOpen]);  // Only depend on isOpen - file is stable when isOpen becomes true

  const loadSheetPreview = async () => {
    if (!file) return;
    if (loading || preview) return;  // Already loading or loaded - don't duplicate
    
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await authenticatedFetch('/excel/preview_sheets', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Preview failed: ${response.statusText}`);
      }

      const result: SheetPreviewResult = await response.json();
      setPreview(result);
      
      // If only one sheet, auto-confirm without showing dialog
      if (result.sheets.length === 1) {
        console.log('[ExcelSheetSelector] Only one sheet found, auto-processing');
        onConfirm([result.sheets[0].name], true);
        return;
      }
      
      // Multiple sheets: default to none selected - user must actively choose
      setSelectedSheets(new Set());
      setSelectAll(false);
      
    } catch (err) {
      console.error('Sheet preview error:', err);
      setError(err instanceof Error ? err.message : 'Failed to preview Excel sheets');
    } finally {
      setLoading(false);
    }
  };

  const handleSheetToggle = (sheetName: string, checked: boolean) => {
    setSelectedSheets(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(sheetName);
      } else {
        newSet.delete(sheetName);
      }
      
      // Update selectAll based on current selection
      if (preview) {
        setSelectAll(newSet.size === preview.sheets.length);
      }
      
      return newSet;
    });
  };

  const handleSelectAllToggle = (checked: boolean) => {
    setSelectAll(checked);
    if (checked && preview) {
      setSelectedSheets(new Set(preview.sheets.map(s => s.name)));
    } else {
      setSelectedSheets(new Set());
    }
  };

  const handleConfirm = () => {
    const sheetsArray = Array.from(selectedSheets);
    // If all sheets selected, pass empty array (backend treats as "all")
    const processAll = selectAll && preview && sheetsArray.length === preview.sheets.length;
    onConfirm(processAll ? [] : sheetsArray, selectAll);
  };

  const formatRows = (rows: number): string => {
    if (rows >= 1000000) return `${(rows / 1000000).toFixed(1)}M`;
    if (rows >= 1000) return `${(rows / 1000).toFixed(1)}K`;
    return rows.toString();
  };

  const formatTime = (seconds: number): string => {
    if (seconds < 60) return `~${seconds} seconds`;
    const minutes = Math.floor(seconds / 60);
    return `~${minutes} minute${minutes > 1 ? 's' : ''}`;
  };

  const getSelectedStats = (): { rows: number; chunks: number } => {
    if (!preview) return { rows: 0, chunks: 0 };
    
    let rows = 0;
    let chunks = 0;
    
    for (const sheet of preview.sheets) {
      if (selectedSheets.has(sheet.name)) {
        rows += sheet.rows;
        chunks += sheet.estimated_chunks || 1;
      }
    }
    
    return { rows, chunks };
  };

  const selectedStats = getSelectedStats();

  // Don't render anything if no file (component is always mounted but hidden)
  if (!file) {
    return null;
  }

  return (
    <Dialog
      hidden={!isOpen}
      onDismiss={onCancel}
      dialogContentProps={{
        type: DialogType.largeHeader,
        title: 'Select Sheets to Process',
        subText: preview?.filename || file.name,
      }}
      modalProps={{
        isBlocking: true,
        styles: { main: { maxWidth: 600, minWidth: 480 } },
      }}
    >
      <Stack tokens={stackTokens}>
        {loading && (
          <Stack horizontalAlign="center" tokens={{ childrenGap: 10, padding: 20 }}>
            <Spinner size={SpinnerSize.large} label="Analyzing Excel file..." />
            <Text variant="small" styles={{ root: { color: '#666' } }}>
              Reading sheet information...
            </Text>
          </Stack>
        )}

        {error && (
          <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
            {error}
            <DefaultButton
              text="Retry"
              iconProps={{ iconName: 'Refresh' }}
              onClick={loadSheetPreview}
              styles={{ root: { marginLeft: 10 } }}
            />
          </MessageBar>
        )}

        {!loading && !error && preview && (
          <>
            {/* Recommendation message */}
            {preview.total_rows > 10000 && (
              <MessageBar messageBarType={MessageBarType.info}>
                <Text variant="small">{preview.recommendation}</Text>
              </MessageBar>
            )}

            {/* Select All checkbox */}
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
              <CheckboxChecked24Regular />
              <Checkbox
                label={`Select All (${preview.sheets.length} sheets)`}
                checked={selectAll}
                onChange={(_, checked) => handleSelectAllToggle(checked || false)}
                styles={{ root: { fontWeight: 600 } }}
              />
            </Stack>

            {/* Sheet list */}
            <Stack 
              styles={{ 
                root: { 
                  maxHeight: 300, 
                  overflowY: 'auto',
                  border: '1px solid #edebe9',
                  borderRadius: 4,
                  padding: 8
                } 
              }}
            >
              {preview.sheets.map((sheet) => (
                <Stack
                  key={sheet.name}
                  horizontal
                  verticalAlign="center"
                  tokens={{ childrenGap: 12, padding: '8px 4px' }}
                  styles={{
                    root: {
                      borderBottom: '1px solid #f3f2f1',
                      ':hover': { backgroundColor: '#f5f5f5' }
                    }
                  }}
                >
                  <Checkbox
                    checked={selectedSheets.has(sheet.name)}
                    onChange={(_, checked) => handleSheetToggle(sheet.name, checked || false)}
                  />
                  <Table24Regular />
                  <Stack grow>
                    <Text variant="medium" styles={{ root: { fontWeight: 500 } }}>
                      {sheet.name}
                    </Text>
                    <Text variant="small" styles={{ root: { color: '#666' } }}>
                      {formatRows(sheet.rows)} rows × {sheet.columns} columns
                    </Text>
                  </Stack>
                  {sheet.estimated_chunks && (
                    <Text variant="tiny" styles={{ root: { color: '#999' } }}>
                      ~{sheet.estimated_chunks} chunks
                    </Text>
                  )}
                </Stack>
              ))}
            </Stack>

            {/* Selection summary */}
            <Stack 
              horizontal 
              horizontalAlign="space-between"
              styles={{ 
                root: { 
                  backgroundColor: '#f5f5f5', 
                  padding: 12, 
                  borderRadius: 4 
                } 
              }}
            >
              <Stack>
                <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
                  Selected: {selectedSheets.size} of {preview.sheets.length} sheets
                </Text>
                <Text variant="small" styles={{ root: { color: '#666' } }}>
                  {formatRows(selectedStats.rows)} rows • ~{selectedStats.chunks} chunks
                </Text>
              </Stack>
              <Stack horizontalAlign="end">
                <Text variant="small" styles={{ root: { color: '#666' } }}>
                  Est. processing time
                </Text>
                <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
                  {formatTime(Math.ceil(selectedStats.rows / 1000) * 2)}
                </Text>
              </Stack>
            </Stack>
          </>
        )}
      </Stack>

      <DialogFooter>
        <DefaultButton onClick={onCancel} text="Cancel" />
        <PrimaryButton
          onClick={handleConfirm}
          text={selectedSheets.size === 0 ? 'Select at least one sheet' : 'Process Selected Sheets'}
          disabled={loading || selectedSheets.size === 0}
        />
      </DialogFooter>
    </Dialog>
  );
};

export default ExcelSheetSelector;
